import React, { useEffect, useRef } from 'react';
import { Box, IconButton, Tooltip, Divider } from '@mui/material';
import {
  FormatBold, FormatItalic, FormatUnderlined,
  FormatListBulleted, FormatListNumbered, Title, LinkOff,
} from '@mui/icons-material';

interface Props {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  minHeight?: number;
}

// Lightweight contentEditable rich-text editor (bold/italic/underline/lists/
// headings). Emits HTML. No external dependency.
export default function RichTextEditor({ value, onChange, placeholder, minHeight = 160 }: Props) {
  const ref = useRef<HTMLDivElement>(null);

  // Sync external value into the editor only when it differs from the live
  // DOM, so typing doesn't reset the caret.
  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== (value || '')) {
      ref.current.innerHTML = value || '';
    }
  }, [value]);

  const exec = (command: string, arg?: string) => {
    document.execCommand(command, false, arg);
    ref.current?.focus();
    if (ref.current) onChange(ref.current.innerHTML);
  };

  const handleInput = () => {
    if (ref.current) onChange(ref.current.innerHTML);
  };

  const btn = (title: string, onClick: () => void, icon: React.ReactNode) => (
    <Tooltip title={title}>
      <IconButton size="small" onMouseDown={(e) => e.preventDefault()} onClick={onClick}>
        {icon}
      </IconButton>
    </Tooltip>
  );

  return (
    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, p: 0.5, bgcolor: 'background.default', borderBottom: 1, borderColor: 'divider', flexWrap: 'wrap' }}>
        {btn('Bold', () => exec('bold'), <FormatBold fontSize="small" />)}
        {btn('Italic', () => exec('italic'), <FormatItalic fontSize="small" />)}
        {btn('Underline', () => exec('underline'), <FormatUnderlined fontSize="small" />)}
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
        {btn('Heading', () => exec('formatBlock', 'H3'), <Title fontSize="small" />)}
        {btn('Bulleted list', () => exec('insertUnorderedList'), <FormatListBulleted fontSize="small" />)}
        {btn('Numbered list', () => exec('insertOrderedList'), <FormatListNumbered fontSize="small" />)}
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
        {btn('Clear formatting', () => exec('removeFormat'), <LinkOff fontSize="small" />)}
      </Box>
      <Box
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        data-placeholder={placeholder}
        sx={{
          minHeight,
          p: 1.5,
          fontSize: 14,
          lineHeight: 1.5,
          outline: 'none',
          overflowY: 'auto',
          '&:empty:before': {
            content: 'attr(data-placeholder)',
            color: 'text.disabled',
          },
          '& ul, & ol': { pl: 3, my: 1 },
          '& h3': { m: 0, mb: 1 },
          '& p': { m: 0 },
        }}
      />
    </Box>
  );
}
